// Minimal i18n. Dictionary lookup + DOM translator + persisted toggle.
import en from './en.js';
import vi from './vi.js';

const dicts = { en, vi };
const LS_KEY = 'hd:v2:lang';
const listeners = new Set();

let current = (() => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && dicts[saved]) return saved;
  } catch {}
  // First-visit default: match browser preference if it's Vietnamese, else English.
  const nav = (typeof navigator !== 'undefined' ? navigator.language || '' : '').toLowerCase();
  return nav.startsWith('vi') ? 'vi' : 'en';
})();

export function getLang() { return current; }

export function setLang(lang) {
  if (!dicts[lang] || lang === current) return;
  current = lang;
  try { localStorage.setItem(LS_KEY, lang); } catch {}
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
    applyTranslations();
  }
  listeners.forEach((fn) => { try { fn(lang); } catch {} });
}

export function onLangChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }

export function t(key, fallback) {
  return dicts[current]?.[key] ?? dicts.en?.[key] ?? (fallback ?? key);
}

/**
 * Walk the DOM and translate elements marked with data-i18n / data-i18n-attr.
 *
 *   <button data-i18n="hdr_team">team view</button>
 *   <input  data-i18n-attr="placeholder:import_dropzone">
 *   <span   data-i18n-html="email_modal_hint"></span>   ← only for trusted keys
 */
export function applyTranslations(root = document) {
  root.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (key) el.textContent = t(key);
  });
  root.querySelectorAll('[data-i18n-attr]').forEach((el) => {
    el.getAttribute('data-i18n-attr').split(',').forEach((pair) => {
      const [attr, key] = pair.split(':').map((s) => s.trim());
      if (attr && key) el.setAttribute(attr, t(key));
    });
  });
}

/** Mount a tiny EN/VI toggle into a container element. */
export function mountLangToggle(container) {
  if (!container) return;
  const wrap = document.createElement('div');
  wrap.id = 'lang-toggle';
  wrap.style.cssText = 'display:flex;gap:2px;font-family:var(--f)';
  ['en', 'vi'].forEach((lang) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = lang.toUpperCase();
    b.dataset.lang = lang;
    b.style.cssText = 'padding:3px 8px;font-size:9px;font-weight:700;letter-spacing:.06em;border:1px solid #2a2a2a;background:transparent;color:#666;cursor:pointer;font-family:var(--f);border-radius:3px';
    b.addEventListener('click', () => setLang(lang));
    wrap.appendChild(b);
  });
  container.appendChild(wrap);
  const sync = () => {
    wrap.querySelectorAll('button').forEach((btn) => {
      const on = btn.dataset.lang === current;
      btn.style.background = on ? '#e6edf3' : 'transparent';
      btn.style.color = on ? '#0d1117' : '#666';
      btn.style.borderColor = on ? '#e6edf3' : '#2a2a2a';
    });
  };
  sync();
  onLangChange(sync);
}
